import { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform, type MotionValue } from 'framer-motion';

export type ValuePillarCard = {
  id: string;
  title: string;
  tagline: string;
  audienceBadge: string;
  bullets: string[];
};

export type StickyAboutScrollSectionProps = {
  sectionTitle: string;
  cards: ValuePillarCard[];
  sectionVh?: number;
};

const VALUE_CARD_SEGMENTS = 4;

export const VALUE_CARD_ICON_SRC: readonly string[] = [
  '/static/images/icons/microscope-svgrepo-com.svg',
  '/static/images/icons/hospital-svgrepo-com.svg',
  '/static/images/icons/brain.png',
  '/static/images/icons/ai-research%20(2).png',
];

function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}

function circlePopIn(p: number, index: number, total: number) {
  const seg = 1 / total;
  const start = index * seg;
  const fade = seg * 0.42;
  if (p <= start) return 0;
  if (p < start + fade) return clamp01((p - start) / fade);
  return 1;
}

function useCircleReveal(scrollYProgress: MotionValue<number>, index: number, total: number) {
  const reveal = useTransform(scrollYProgress, p => circlePopIn(p, index, total));
  const scale = useTransform(reveal, o => 0.52 + 0.48 * o);
  return { opacity: reveal, scale };
}

type OrbitMotion = { opacity: MotionValue<number>; scale: MotionValue<number> };

function ValueCardWithOrbit({
  card,
  iconSrc,
  orbitMotion,
}: {
  card: ValuePillarCard;
  iconSrc: string;
  orbitMotion?: OrbitMotion;
}) {
  const cardBody = (
    <>
      <h3 className="scrolly-value-card__title">{card.title}</h3>
      <p className="scrolly-value-card__tagline">{card.tagline}</p>
      <ul className="scrolly-value-card__list">
        {card.bullets.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
      <div className="scrolly-value-badge-row">
        {orbitMotion ? (
          <motion.p className="scrolly-value-audience-badge" style={{ opacity: orbitMotion.opacity }}>
            {card.audienceBadge}
          </motion.p>
        ) : (
          <p className="scrolly-value-audience-badge">{card.audienceBadge}</p>
        )}
      </div>
    </>
  );

  return (
    <div className="scrolly-value-card-wrap">
      <div className="scrolly-value-orbit-anchor">
        {orbitMotion ? (
          <motion.div className="scrolly-value-orbit" style={orbitMotion} aria-hidden>
            <img src={iconSrc} alt="" draggable={false} decoding="async" />
          </motion.div>
        ) : (
          <div className="scrolly-value-orbit" aria-hidden>
            <img src={iconSrc} alt="" draggable={false} decoding="async" />
          </div>
        )}
      </div>
      <div className="scrolly-value-card-shell">
        {orbitMotion ? (
          <motion.article className="scrolly-value-card" style={{ opacity: orbitMotion.opacity }}>
            {cardBody}
          </motion.article>
        ) : (
          <article className="scrolly-value-card">{cardBody}</article>
        )}
      </div>
    </div>
  );
}

function ValueSectionStatic({ sectionTitle, cards }: { sectionTitle: string; cards: ValuePillarCard[] }) {
  const displayCards = cards.slice(0, VALUE_CARD_SEGMENTS);
  return (
    <div className="scrolly-value-fallback">
      <div className="scrolly-value-fallback__inner">
        <h2 className="scrolly-value-heading">{sectionTitle}</h2>
        <div className="scrolly-value-fallback__grid">
          {displayCards.map((card, i) => (
            <ValueCardWithOrbit
              key={card.id}
              card={card}
              iconSrc={VALUE_CARD_ICON_SRC[i] ?? VALUE_CARD_ICON_SRC[0]}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function StickyAboutScrollSectionAnimated({
  sectionTitle,
  cards,
  sectionVh = 400,
}: StickyAboutScrollSectionProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: rootRef,
    offset: ['start start', 'end end'],
  });

  const total = VALUE_CARD_SEGMENTS;
  const c0 = useCircleReveal(scrollYProgress, 0, total);
  const c1 = useCircleReveal(scrollYProgress, 1, total);
  const c2 = useCircleReveal(scrollYProgress, 2, total);
  const c3 = useCircleReveal(scrollYProgress, 3, total);
  const motions: OrbitMotion[] = [c0, c1, c2, c3];

  const displayCards = cards.slice(0, VALUE_CARD_SEGMENTS);

  return (
    <div ref={rootRef} className="scrolly-value-root" style={{ height: `${sectionVh}vh` }}>
      <div className="scrolly-value-sticky scrolly-value-sticky--grid">
        <h2 className="scrolly-value-heading scrolly-value-heading--sticky">{sectionTitle}</h2>
        <div className="scrolly-value-grid scrolly-value-grid--scroll">
          {displayCards.map((card, i) => (
            <ValueCardWithOrbit
              key={card.id}
              card={card}
              iconSrc={VALUE_CARD_ICON_SRC[i] ?? VALUE_CARD_ICON_SRC[0]}
              orbitMotion={motions[i]}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function StickyAboutScrollSection(props: StickyAboutScrollSectionProps) {
  const reducedMotion = useReducedMotion();
  if (reducedMotion) {
    return <ValueSectionStatic sectionTitle={props.sectionTitle} cards={props.cards} />;
  }
  return <StickyAboutScrollSectionAnimated {...props} />;
}

export function ValuePropositionMobileSection(props: StickyAboutScrollSectionProps) {
  return <ValueSectionStatic sectionTitle={props.sectionTitle} cards={props.cards} />;
}
